import { Numbers } from "@utils/Numbers";
import classNames from "classnames/bind";
import { useState } from "react";

import { Badge, Button, Drawer, Input, Label } from "@components";
import { Oraculo } from "@core/oraculo";
import OcdpAPI from "@providers/OcdpAPI";
import styles from "@styles/containers/cdp/audiences.module.scss";
import ROLES from "data/Roles";
import OracleDrawer from "shared/containers/oraculo/OracleDrawer";
import { UserStorage } from "shared/tools/UserStorage";
import { If } from "shared/components/control-statements";
import OcdpAPI2 from "providers/OcdpAPI2";
import { GodMode } from "shared/core/GodMode";
const css = classNames.bind(styles);

const TYPE_LABEL = {
  STATIC: "Estático",
  DYNAMIC: "Dinâmico",
};

export const AudienceTableItem = ({
  audience,
  onSelect,
  onSaved,
  onDeleted,
  selectedAudience,
  onOpenHistory,
  archived,
  collapsed,
  setCollapsed,
}) => {
  const [visible, setVisible] = useState(false);
  const [in_progress, setInProgress] = useState(false);
  const [openRename, setOpenRename] = useState(false);
  const [name, setName] = useState(audience.name);
  const [loading_rename, setLoadingRename] = useState(false);

  const selectedAudienceId =
    typeof selectedAudience === "number"
      ? selectedAudience
      : selectedAudience?.id;

  const is_selected = selectedAudienceId === audience.id;

  const toggleAudience = () => onSelect(!is_selected ? audience : null);

  const deleteFn = async () => {
    const ocdp = new OcdpAPI2();
    const response = await ocdp.delete_audience(audience.id);

    if (!response) {
      return alert({ text: "Ocorreu um erro ao excluir o público" });
    }

    if (onDeleted) {
      onDeleted(audience);
    }
    setVisible(false);
  };

  const save_fn = async ({ boxes, links, schema }) => {
    try {
      setInProgress(true);

      const ocdp = new OcdpAPI2();
      const response = await ocdp.upsert_audience(audience.id, {
        name: audience.name,
        type: audience.type,
        filters: audience.filters,
        boxes: boxes,
        links: links,
        schema: schema,
      });

      if (!response?.execution_id) {
        throw new Error("Ocorreu um erro durante o salvamento do público");
      }

      const exec = await Oraculo.waitQueryExecution(response.execution_id);
      if (exec?.status == "ERROR") {
        throw new Error("Ocorreu um erro durante o salvamento do público");
      }

      onSaved && onSaved();
      setVisible(false);
    } catch (err) {
      alert({ text: err.message });
    }
    setInProgress(false);
  };

  const rename_fn = async () => {
    setLoadingRename(true);
    try {
      const ocdp = new OcdpAPI2();
      const response = await ocdp.rename_audience(audience.id, name);
      if (!response) {
        throw new Error("Ocorreu um erro ao renomear o público");
      }
      setOpenRename(false);
      onSaved && onSaved();
    } catch (err) {
      alert({ text: err.message });
    }
    setLoadingRename(false);
  };

  const hide_fn = async () => {
    const ocdp = new OcdpAPI2();
    const response = await ocdp.hide_audience(audience.id);
    if (!response) {
      throw new Error("Ocorreu um erro ao ocultar o público");
    }
    onSaved && onSaved();
  };

  const isDefaultAudience = () => {
    return (
      audience.name?.trimStart().startsWith("[Olga] ") ||
      audience.name?.trimStart().toLowerCase().startsWith("[bloqueado] ")
    );
  }

  if (!UserStorage.isGodMode) {
    if (audience.name?.trimStart().toLowerCase().startsWith("[bloqueado] ")) {
      return;
    }

    if (isDefaultAudience() && audience.state == "HIDDEN") {
      return;
    }
  }

  return (
    <>
      <tr className={css({ audience: true, table: true })}>
        <td onClick={toggleAudience}>
          <div className="d-flex align-center">
            <div className={css({ icon: true, is_selected })}>
              {/* {is_selected ? (
                <Radio value={is_selected} checked={is_selected} readOnly />
              ) : (
                <i className="fas fa-users" />
              )} */}
              <i className="fas fa-users" />
            </div>
            <If cond={audience.version === "V1"}>
              <Badge
                type="default"
                name="Antiga versão de público"
                absolute={false}
                className="mr-10"
              />
            </If>
            <p>{audience.name}</p>
            <If cond={audience.state == "HIDDEN"}>
              <Badge
                type="default"
                name="Oculto"
                absolute={false}
                className="ml-10"
              />
            </If>
          </div>
        </td>
        {/* <Cell onClick={toggleAudience}>
          <p className={css({ inUse: false })}>{"---"}</p>
        </Cell> */}
        <Cell onClick={toggleAudience} data={TYPE_LABEL[audience.type]} />
        <Cell
          onClick={toggleAudience}
          data={`${Numbers.formatInt(audience.qtd_customers || 0)} pessoas`}
        />
        <If cond={audience.version === "V2"}>
          <Cell>
            <div className="d-flex align-start">
              <p
                className={css({
                  growth: true,
                  up: audience.qtd_customers > audience.previous_qtd_customers,
                  down:
                    audience.qtd_customers < audience.previous_qtd_customers,
                })}
              >
                {Math.abs(
                  audience.qtd_customers - audience.previous_qtd_customers
                )}{" "}
                <If
                  cond={
                    Math.abs(
                      audience.qtd_customers - audience.previous_qtd_customers
                    ) > 0
                  }
                >
                  {audience.qtd_customers > audience.previous_qtd_customers ? (
                    <i className="fal fa-arrow-trend-up" />
                  ) : (
                    <i className="fal fa-arrow-trend-down" />
                  )}
                </If>
              </p>
            </div>
          </Cell>
        </If>
        <If cond={audience.version === "V1"}>
          <Cell>
            <span>---</span>
          </Cell>
        </If>

        <td>
          <div
            className={styles.collapseActions}
            onClick={() =>
              setCollapsed(collapsed === audience.id ? null : audience.id)
            }
          >
            <div className={styles.action}>
              <i className="fal fa-ellipsis-v" />
            </div>

            <div
              className={css({
                collapsedActions: true,
                in: collapsed === audience.id,
                out: collapsed !== audience.id,
              })}
            >
              <ul className={styles.options}>
                {GodMode.isGodMode && (
                  <li
                    onClick={() => {
                      // Copiar para área de transferência o id do público
                      navigator.clipboard.writeText(audience.id);
                      alert({ text: "ID do público copiado para área de transferência", buttonText: "Ok" });
                    }}
                  >
                    Id: {audience.id}
                  </li>
                )}

                {!archived && (
                  <li
                    onClick={() => {
                      setOpenRename(true);
                      setCollapsed(false);
                    }}
                  >
                    Renomear
                  </li>
                )}

                <If cond={audience.version === "V2"}>
                  <li
                    onClick={() => {
                      onOpenHistory(audience);
                      setCollapsed(false);
                    }}
                  >
                    Ver histórico
                  </li>
                </If>

                {!archived && (
                  <li
                    onClick={() => {
                      setCollapsed(false);
                      deleteFn();
                    }}
                  >
                    Arquivar público
                  </li>
                )}
                <If cond={UserStorage.isGodMode && isDefaultAudience() && audience.state == "ACTIVE"}>
                  <li
                    onClick={() => {
                      setCollapsed(false);
                      hide_fn();
                    }}
                  >
                    Ocultar público
                  </li>
                </If>
              </ul>
            </div>
          </div>
        </td>
      </tr>

      <OracleDrawer
        visible={visible}
        onQuery={({ boxes, links, schema }) => {
          save_fn({ boxes, links, schema });
        }}
        onDelete={() => deleteFn(audience)}
        boxes={audience.boxes}
        links={audience.links}
        onClose={() => setVisible(false)}
        audience={audience}
        in_progress={in_progress}
      />

      <Drawer
        visible={openRename}
        onClose={() => setOpenRename(false)}
        title="Renomear público"
      >
        <Label>Insira o nome do público</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} />

        <div className={styles.footer}>
          <Button
            className="w-100"
            design="primary"
            onClick={rename_fn}
            loading={loading_rename}
            disabled={loading_rename}
          >
            Salvar
          </Button>
        </div>
      </Drawer>
    </>
  );
};

const Cell = ({ children, data, onClick, className }) => {
  return (
    <td onClick={onClick} className={className}>
      <div>{children ? children : <p>{data}</p>}</div>
    </td>
  );
};
